import { TextInput, CustomButton, PhoneInput } from '@/shared/ui/components';
import { phoneFormat } from '@/shared/lib';
import { t } from 'ttag';
import { useSecondDrawer } from './useSecondDrawer';
import type { SecondDrawerProps } from './SecondDrawer.types';
import './style.scss';

/**
 * Renders the secondary patient contact drawer with optional name, relationship, phone and email fields bound to the useSecondDrawer state. The phone value is run through the country-aware phone formatter and offers the country list as options, while phone and email surface their validation errors and cancel/save reflect the saving flag.
 *
 * @param data - Existing secondary contact values used to seed the form.
 * @param handleClick - Callback closing the drawer, also wired to the cancel button.
 * @param isSaving - Whether a save request is in flight, disabling cancel and loading save.
 * @param countries - Country options offered by the phone number country picker.
 * @returns The secondary contact editing form with action buttons.
 */
const SecondDrawer = ({ data, handleClick, isSaving, countries }: SecondDrawerProps) => {
  const { state, handleChange, onClickBottomButton } = useSecondDrawer({ data, handleClick });

  return (
    <div className="list-input">
      <TextInput
        name="name"
        label={
          <div>
            {t`Name`} <span className="optional-label">(optional)</span>
          </div>
        }
        placeholder={t`Enter name`}
        value={state.name}
        onChange={handleChange}
      />
      <TextInput
        name="relationship"
        label={
          <div>
            {t`Relationship`} <span className="optional-label">(optional)</span>
          </div>
        }
        placeholder={t`Enter relationship`}
        value={state.relationship}
        onChange={handleChange}
      />
      <PhoneInput
        title={
          <div>
            {t`Phone number`} <span className="optional-label">(optional)</span>
          </div>
        }
        name="phone"
        countryValue={state.country}
        value={phoneFormat(state.phone, state.country)}
        placeholder="800-555-1234"
        onChange={handleChange}
        size="small"
        isError={!!state.errorObject.phone}
        errorMessage={state.errorObject.phone}
        maxLength={15}
        countryOptions={countries}
      />
      <TextInput
        name="email"
        label={
          <div>
            {t`Email`} <span className="optional-label">(optional)</span>
          </div>
        }
        placeholder="johnsmith@mail.com"
        value={state.email}
        onChange={handleChange}
        isError={!!state.errorObject.email}
        errorMessage={state.errorObject.email}
      />

      <div className="bottom-button-row">
        <CustomButton
          id="cancel-btn"
          style="neutral"
          type="outline"
          onClick={handleClick}
          styles={{ minWidth: 80 }}
          disabled={isSaving}
        >
          {t`Cancel`}
        </CustomButton>
        <CustomButton
          id="save-btn"
          onClick={onClickBottomButton}
          styles={{ minWidth: 80 }}
          loading={isSaving}
        >
          {t`Save`}
        </CustomButton>
      </div>
    </div>
  );
};

export default SecondDrawer;
